import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Plus } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { OrderStatsQuery, SearchFilters } from '@/types/order'
import type { AxiosError } from 'axios'
import type { ErrorResponse } from '@/types/common'
import type { ExcelColumn } from '@/lib/excel-export'
import HeaderTitle from '@/components/TitleHeader'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { OrdersTable, StatsCard } from '@/components/Orders'
import { useExportOrders, useOrdersStats } from '@/hooks/useOrders'
import { OrdersService } from '@/services/orders'
import { ExcelExporter } from '@/lib/excel-export'
import { formatCurrency, formatPaymentDisplay } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/products/orders/')({
  component: RouteComponent,
})

function RouteComponent() {
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year'>('week')

  // Fetch orders statistics (without date range)
  const statsQuery: OrderStatsQuery = {}
  const {
    data: statsResponse,
    isLoading: statsLoading,
    error: statsError,
  } = useOrdersStats(statsQuery)
  const stats = statsResponse?.data

  // Export orders mutation
  const exportOrdersMutation = useExportOrders()

  const handleSearch = (filters: SearchFilters) => {
    // Search is handled by the OrdersTable component
    console.log('Searching with filters:', filters)
  }

  const clearFilters = () => {
    // Clear filters is handled by the OrdersTable component
    console.log('Clearing filters')
  }

  const mutationExport = useMutation({
    mutationFn: () => {
      return OrdersService.getOrders({ page: 1, take: 100, isSelectAll: true })
    },
    onSuccess: (res) => {
      const data = res.data.items.map((order) => ({
        code: order.code,
        userName: order.user.userName,
        productNames: order.items.map((item) => item.product.name).join(', '),
        totalQuantity: order.items.reduce(
          (sum, item) => sum + item.quantity,
          0,
        ),
        totalPrice: formatPaymentDisplay(
          order.subtotalAmountMinor || 0,
          order.currency,
        ),
        status: order.status,
      }))
      ExcelExporter.export(data, ordersExcelColumns, {
        filename: `danh-sach-don-hang-${new Date().toISOString().split('T')[0]}.xlsx`,
        sheetName: 'Danh sách đơn hàng',
      })
      toast.success('Export Excel thành công!')
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message || 'Export thất bại!')
    },
  })

  const ordersExcelColumns: ExcelColumn[] = [
    { key: 'code', label: 'Mã đơn hàng' },
    { key: 'userName', label: 'Username' },
    { key: 'productNames', label: 'Sản phẩm' },
    { key: 'totalQuantity', label: 'Tổng số lượng' },
    { key: 'totalPrice', label: 'Tổng giá' },
    { key: 'status', label: 'Trạng thái' },
  ]

  const handleExportData = () => {
    mutationExport.mutate()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <HeaderTitle
        title="Đơn hàng"
        subtitle="Chào mừng bạn đến với Trung tâm thông tin dữ liệu của bạn!"
        action={
          <div className="flex gap-2">
            <Button
              variant="default"
              className="gap-2"
              onClick={handleExportData}
              disabled={exportOrdersMutation.isPending}
            >
              <Plus className="h-4 w-4" />
              {exportOrdersMutation.isPending ? 'Đang xuất...' : 'Xuất dữ liệu'}
            </Button>
            <Select
              value={timeRange}
              onValueChange={(value: 'week' | 'month' | 'year') =>
                setTimeRange(value)
              }
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Theo tuần" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Theo tuần</SelectItem>
                <SelectItem value="month">Theo tháng</SelectItem>
                <SelectItem value="year">Theo năm</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatsCard
          title="Tổng doanh thu"
          value={
            statsLoading ? (
              'Đang tải...'
            ) : statsError ? (
              'Lỗi tải dữ liệu'
            ) : (
              <div className="flex flex-col gap-1">
                <span>
                  {formatCurrency(stats?.revenue.vnd?.total || 0, 'vnd')} |
                  {` ${formatCurrency(stats?.revenue.usd?.total || 0, 'usd')}`}
                </span>
              </div>
            )
          }
          growth={
            statsLoading ? (
              ''
            ) : statsError ? (
              ''
            ) : (
              <span>
                {`${stats?.revenue.vnd?.growthPercentage || 0}% so với tháng trước`}{' '}
                |
                {` ${stats?.revenue.usd?.growthPercentage || 0}% so với tháng trước`}
              </span>
            )
          }
          growthLabel={statsLoading ? '' : ''}
        />
        <StatsCard
          title="Đơn hàng đã bán"
          value={
            statsLoading
              ? 'Đang tải...'
              : statsError
                ? 'Lỗi tải dữ liệu'
                : `${stats ? stats.orders.total : 0} đơn`
          }
          growth={stats ? stats.orders.growthPercentage : 0}
          growthLabel={statsLoading ? '' : 'so với tháng trước'}
        />
      </div>

      {/* Orders Table */}
      <OrdersTable onSearch={handleSearch} onClearFilters={clearFilters} />
    </div>
  )
}
